import {
  Modal,
  ModalDialog,
  DialogContent,
  Stack,
  Textarea,
  Divider,
  Box,
  Tooltip,
} from "@mui/joy";
import DarkInput from "../../common/DarkInput";
import type { EquipmentModel } from "../../../api/equipment/Equipment.typed";
import { SClose, SDone } from "../../../styles/StyledMUI";
import EquipmentAttributesList from "./EquipmentAttributesList";
import EquipmentAttributeForm from "./EquipmentAttributeForm";
import EquipmentPrefiltersList from "./EquipmentPrefiltersList";
import EquipmentModelAlertsEditor from "./EquipmentModelAlertsEditor";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, textTokens, withAlpha } from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";

interface EquipmentModelModalProps {
  open: boolean;
  onClose: () => void;
  model: EquipmentModel | null;
  onModelChange: (model: EquipmentModel) => void;
  onSave: () => void;
  onOpenOptions: (idx: number) => void;
  onOpenAlert: (idx: number) => void;
}

export default function EquipmentModelModal({
  open,
  onClose,
  model,
  onModelChange,
  onSave,
  onOpenOptions,
  onOpenAlert,
}: EquipmentModelModalProps) {
  if (!model) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock={false}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: withAlpha(colorTokens.bg.overlay, 0.6),
            backdropFilter: "blur(4px)",
          },
        },
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <ModalDialog
        variant="outlined"
        sx={{
          bgcolor: colorTokens.bg.surface,
          border: `1px solid ${colorTokens.border.default}`,
          p: 3,
          borderRadius: 12,
          width: { xs: "95vw", md: "80vw" },
          maxHeight: "90vh",
          boxShadow: `0 8px 32px ${withAlpha(colorTokens.bg.overlay, 0.4)}`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "hidden",
        }}
      >
        <DarkTypography level="h2" colorVariant="title" sx={{ fontWeight: 600 }}>
          {model.Name || "Nouveau modèle"}
        </DarkTypography>
        <DarkTypography level="body-sm" colorVariant="primary">
          Gérez les attributs de ce modèle.
        </DarkTypography>
        <Divider sx={{ bgcolor: colorTokens.border.default }} />

        <DialogContent
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#333",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#555",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#777",
              },
            },
          }}
        >
          <Stack spacing={2} sx={{ mb: 2 }}>
            <DarkInput
              placeholder="Nom du modèle"
              value={model.Name || ""}
              onChange={(e) => onModelChange({ ...model, Name: e.target.value })}
            />
            <Textarea
              placeholder="Description (2 lignes)"
              minRows={2}
              value={model.Description || ""}
              onChange={(e) => onModelChange({ ...model, Description: e.target.value })}
              sx={{
                backgroundColor: colorTokens.bg.surfaceMuted,
                color: textTokens.color.primary,
                border: `1px solid ${colorTokens.border.default}`,
                borderRadius: "8px",
                "&:hover": { borderColor: colorTokens.accent.hover },
                "&:focus-within": { borderColor: colorTokens.accent.primary },
              }}
            />
          </Stack>

          <DarkTypography level="h4" colorVariant="title" sx={{ mt: 1, fontWeight: 600 }}>
            Attributs
          </DarkTypography>

          <EquipmentAttributesList
            model={model}
            onModelChange={onModelChange}
            onOpenOptions={onOpenOptions}
            onOpenAlert={onOpenAlert}
          />

          <EquipmentAttributeForm model={model} onModelChange={onModelChange} />

          <Divider sx={{ my: 3, bgcolor: colorTokens.border.default }} />

          <DarkTypography level="h4" colorVariant="title" sx={{ mb: 2, fontWeight: 600 }}>
            Filtres rapides
          </DarkTypography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <EquipmentPrefiltersList model={model} onModelChange={onModelChange} />
            {model.Attributes?.length === 0 && (
              <DarkTypography level="body-sm" colorVariant="muted" sx={{ fontStyle: "italic" }}>
                Vous devez d'abord ajouter des attributs avant de pouvoir créer des filtres.
              </DarkTypography>
            )}

            <Divider sx={{ my: 3, bgcolor: colorTokens.border.default }} />

            <DarkTypography level="h4" colorVariant="title" sx={{ mb: 1, fontWeight: 600 }}>
              Alertes globales
            </DarkTypography>
            <EquipmentModelAlertsEditor model={model} onModelChange={onModelChange} />
          </Box>
        </DialogContent>

        <Divider sx={{ mt: 1, bgcolor: colorTokens.border.default }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
          <Tooltip title="Annuler">
            <IconButtonCTA
              ctaVariant="danger"
              aria-label="Annuler édition modèle"
              variant="plain"
              onClick={onClose}
            >
              <SClose />
            </IconButtonCTA>
          </Tooltip>
          <Tooltip title="Sauvegarder">
            <IconButtonCTA
              ctaVariant="success"
              aria-label="Sauvegarder modèle"
              variant="plain"
              onClick={onSave}
            >
              <SDone />
            </IconButtonCTA>
          </Tooltip>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
